import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useParams } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tag, Plus, Search, AlertCircle, Target, ShieldCheck, ChevronDown,
  Bookmark, Share2, SquareArrowOutUpRight,
} from "lucide-react";
import { Link } from "wouter";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Offer, Product } from "@shared/schema";
import { optimizedImg } from "@/lib/img";
import { personaNameTier } from "@/components/persona-build-card";
import { OfferingBuildCard, OfferingBuildFailedCard } from "@/components/offering-build-card";
import { computeOfferStrength, isOfferStrengthReady, type OfferStrengthInput } from "@shared/offer-strength";

// ── Shared design tokens (persona-card design language) ─────────────────────
// The offering card deliberately reuses the persona card's look: same serif
// stack, same warm near-black band, same fixed-height uniform-grid treatment.

const SERIF = { fontFamily: `'Iowan Old Style', 'Palatino Linotype', Georgia, 'Times New Roman', serif` };
const BAND = "#231c16";

// Every offering card renders to one uniform height (see personas.tsx for the
// full rationale): the hero band is fixed, and the chip / quote bands reserve
// their space whether or not the offering has the data.
const CARD_MIN_H = "min-h-[480px]";

// Title type classes keyed by personaNameTier — the full offering name is
// always shown; longer names step down in size and wrap instead.
const TITLE_TIER_CLASS: Record<ReturnType<typeof personaNameTier>, string> = {
  base: "text-[22px] md:text-[24px] leading-[24px] md:leading-[26px]",
  medium: "text-[17px] md:text-[18px] leading-[21px] md:leading-[22px]",
  small: "text-[14px] md:text-[15px] leading-[18px] md:leading-[19px]",
  xs: "text-[12.5px] md:text-[13px] leading-[16px] md:leading-[17px]",
};

const FUNNEL_STAGE_LABELS: Record<string, string> = {
  tof: "Top of funnel",
  mof: "Mid funnel",
  bof: "Bottom of funnel",
  retention: "Retention",
};

// Pulls the first usable line of text out of a jsonb field that may be a
// string, an array of strings, or an array of objects with assorted keys.
function firstText(v: unknown, depth = 0): string | null {
  if (v == null || depth > 3) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t || null;
  }
  if (Array.isArray(v)) {
    for (const item of v) {
      const t = firstText(item, depth + 1);
      if (t) return t;
    }
    return null;
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    for (const key of ["persona_name", "text", "value", "label", "title", "name"]) {
      const t = firstText(o[key], depth + 1);
      if (t) return t;
    }
    for (const val of Object.values(o)) {
      const t = firstText(val, depth + 1);
      if (t) return t;
    }
  }
  return null;
}

// targetPersonas may hold raw persona ids — never show a UUID on the card.
const isUuidLike = (s: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

function getInitials(name: string): string {
  return name.split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function offerStrength(o: Offer, hasHeroPhoto: boolean): number {
  return computeOfferStrength({ ...(o as OfferStrengthInput), hasHeroPhoto });
}

// Compact side-by-side insight chip (persona TraitChip design): icon + label
// row, then ~2 short lines of condensed text. Height-locked — the text slot
// always reserves two lines so the chip band lines up across every card.
// Missing values render a quiet placeholder rather than dropping the chip.
function InsightChip({ label, text, icon: Icon, tint, testId }: {
  label: string;
  text: string | null;
  icon: typeof Tag;
  tint: { icon: string; iconBg: string; label: string };
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-card/60 px-2 py-2.5 min-w-0" data-testid={testId}>
      <div className="flex items-center gap-1 min-w-0">
        <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border ${tint.iconBg}`}>
          <Icon className={`h-3 w-3 ${tint.icon}`} />
        </div>
        <p className={`text-[8.5px] leading-[13px] font-bold uppercase tracking-[0.08em] truncate ${tint.label}`}>{label}</p>
      </div>
      <p className={`text-[11px] leading-[15px] h-[30px] line-clamp-2 ${text ? "text-muted-foreground" : "text-muted-foreground/45 italic"}`}>
        {text || "Not captured yet"}
      </p>
    </div>
  );
}

function StrengthGauge({ pct }: { pct: number }) {
  const C = 2 * Math.PI * 16; // r=16 in a 40x40 viewBox
  return (
    <div className="relative w-[44px] h-[44px] flex-shrink-0" data-testid="gauge-offer-strength">
      <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
        <circle cx="20" cy="20" r="16" fill="none" strokeWidth="3.5" className="stroke-muted/70" />
        <circle
          cx="20" cy="20" r="16" fill="none" strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * C} ${C}`}
          className={isOfferStrengthReady(pct) ? "stroke-emerald-500" : "stroke-emerald-600/80"}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold tabular-nums">{pct}</span>
    </div>
  );
}

function OfferingCard({ offer, brandId, heroPhoto, index }: {
  offer: Offer;
  brandId: string;
  heroPhoto: string | null;
  index: number;
}) {
  const [objectionOpen, setObjectionOpen] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  // Build-with-AI drafts: while the persist-first generation runs the row
  // renders as an animated build card; a failed build gets retry/discard.
  // The branch lives INSIDE this always-mounted component so the one-time
  // reveal below can see the building→done transition.
  const isFailed = offer.buildStatus === "failed";
  const isBuilding = offer.buildStatus === "building" && !isFailed;

  // One-time reveal: when the build this mounted card was watching resolves
  // successfully, the finished offering card animates in. Offerings that
  // mount already-done never get the animation (the ref starts false).
  const wasBuildingRef = useRef(isBuilding);
  const [reveal, setReveal] = useState(false);
  useEffect(() => {
    if (wasBuildingRef.current && !isBuilding && !isFailed) {
      wasBuildingRef.current = false;
      setReveal(true);
      const t = window.setTimeout(() => setReveal(false), 900);
      return () => window.clearTimeout(t);
    }
    wasBuildingRef.current = isBuilding;
  }, [isBuilding, isFailed]);

  const pct = offerStrength(offer, !!heroPhoto);
  const ready = isOfferStrengthReady(pct);
  const isActive = offer.status === "active";

  // Band image chain: generated AI hero (product-based, lands in the
  // background) → real-photo chain (heroPhoto prop) → initials placeholder.
  // The strength gauge deliberately keeps scoring the REAL-photo chain so an
  // auto-generated hero doesn't inflate offering strength.
  const displayHero = offer.heroImageUrl || heroPhoto;

  const typeLabel = offer.offerType ? offer.offerType.replace(/_/g, " ") : null;

  // Meta line: audience (persona fit / target personas) • funnel stage.
  const personaFit = Array.isArray(offer.personaFit) ? (offer.personaFit as any[]) : [];
  const rawAudience = firstText(personaFit[0]?.persona_name) || firstText(offer.targetPersonas);
  const audience = rawAudience && !isUuidLike(rawAudience) ? `For ${rawAudience}` : null;
  const stages = Array.isArray(offer.targetFunnelStage)
    ? (offer.targetFunnelStage as unknown[]).filter((s): s is string => typeof s === "string")
    : typeof offer.targetFunnelStage === "string" ? [offer.targetFunnelStage] : [];
  const funnelLabel = stages.length
    ? FUNNEL_STAGE_LABELS[stages[0]] || stages[0].replace(/_/g, " ")
    : null;
  const metaParts = [audience, funnelLabel].filter(Boolean) as string[];

  const description = (offer.shortDescription || offer.summary || "").trim() || null;

  // Insight chips from the structured offering fields.
  const buyerProblem = firstText((offer.buyerProblem as any)?.primary_problem);
  const desiredOutcome = firstText((offer.desiredOutcome as any)?.primary_outcome);
  const vp = offer.valueProposition as any;
  const valueProp = firstText(vp?.one_liner) || firstText(vp?.core_benefits);

  // Quote strip: prefer a punchy creative hook; fall back to the value-prop
  // one-liner (the hook reads like the mockup's serif quote, and the
  // one-liner is already surfaced in the Value Prop chip).
  const quote = firstText((offer.creativeInputs as any)?.hooks) || firstText(vp?.one_liner);
  // Renders only once the one-shot generation has landed (missing/failed
  // sketches simply collapse the slot, no broken image or empty gap).
  const sketch = offer.sketchImageUrl || null;
  const objections = Array.isArray(offer.objections) ? (offer.objections as any[]) : [];
  const objection = firstText(objections[0]?.objection);
  const objectionResponse = firstText(objections[0]?.response);

  const nameTier = personaNameTier(offer.name);

  if (isFailed) {
    return <OfferingBuildFailedCard offer={offer} brandId={brandId} index={index} />;
  }
  if (isBuilding) {
    return <OfferingBuildCard offer={offer} index={index} />;
  }

  return (
    <Link href={`/brands/${brandId}/offers/${offer.id}`}>
      <Card
        className={`persona-card group relative cursor-pointer h-full ${CARD_MIN_H} overflow-hidden rounded-2xl border-border/40 hover:border-border/80 hover:shadow-lg transition-all duration-200 ${reveal ? "persona-reveal" : ""}`}
        style={{ animationDelay: `${index * 80}ms` }}
        data-testid={`card-offer-${offer.id}`}
      >
        <CardContent className="p-0 flex flex-col h-full">
          {/* ── Dark cinematic hero band ─────────────────────────────── */}
          <div className="relative overflow-hidden h-[200px] md:h-[210px] flex-shrink-0 flex" style={{ backgroundColor: BAND }}>
            {displayHero ? (
              <div className="absolute inset-0 md:inset-y-0 md:left-auto md:right-0 md:w-[58%]" data-testid={`img-hero-${offer.id}`}>
                <img
                  src={optimizedImg(displayHero, 1280)}
                  alt={offer.name}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
                {/* Blend into the dark band. Overlay extends a few px past the
                    image's left edge so subpixel rounding at fractional
                    percentage widths can never leave an uncovered hairline. */}
                <div
                  className="absolute inset-y-0 -left-1 right-0 pointer-events-none"
                  style={{ background: `linear-gradient(to right, ${BAND} 0%, ${BAND}cc 18%, ${BAND}33 55%, transparent 100%)` }}
                />
                <div
                  className="absolute inset-x-0 bottom-0 h-16 pointer-events-none md:hidden"
                  style={{ background: `linear-gradient(to top, ${BAND}e6, transparent)` }}
                />
              </div>
            ) : (
              <div className="absolute inset-y-0 right-0 w-[50%] flex items-center justify-center bg-gradient-to-l from-white/[0.07] via-white/[0.03] to-transparent pointer-events-none">
                <span className="text-6xl font-bold text-white/10 tracking-wide">{getInitials(offer.name)}</span>
              </div>
            )}

            {/* Fixed-height band, flexible name slot: names are never
                truncated — long names step down in type size and wrap. Meta
                stays one line and description two, so the band never grows. */}
            <div className="relative z-10 flex flex-col justify-center gap-2 p-5 w-full md:max-w-[52%] overflow-hidden">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur-sm">
                  <Tag className="h-3 w-3" />Offering
                </span>
                {typeLabel && (
                  <span className="inline-flex w-fit items-center rounded-full bg-white/10 border border-white/15 px-2.5 py-0.5 text-[8.5px] font-semibold capitalize tracking-[0.06em] text-white/70 backdrop-blur-sm" data-testid={`badge-type-${offer.id}`}>
                    {typeLabel}
                  </span>
                )}
              </div>
              <h3
                className={`text-[#f5efe4] tracking-tight ${TITLE_TIER_CLASS[nameTier]}`}
                style={SERIF}
                data-testid={`text-name-${offer.id}`}
              >
                {offer.name}
              </h3>
              <p className="text-[11.5px] leading-[16px] h-[16px] truncate text-white/75 font-medium" data-testid={`text-meta-${offer.id}`}>
                {metaParts.map((part, i) => (
                  <span key={i}>
                    {i > 0 && <span className="mx-1.5 text-white/40">•</span>}
                    {part}
                  </span>
                ))}
              </p>
              <p className="text-[12px] text-white/55 leading-[17px] h-[34px] line-clamp-2 mt-0.5" data-testid={`text-description-${offer.id}`}>
                {description || "No description yet — click to add offering details"}
              </p>
            </div>
          </div>

          {/* ── Buyer Problem / Desired Outcome / Value Prop chips ───── */}
          {/* Three equal-width chips in one horizontal row. All three slots
              always render (quiet placeholder when a field is missing) so the
              chip band is the same height on every card. */}
          <div className="grid grid-cols-3 gap-2 px-4 pt-4">
            <InsightChip
              label="Buyer Problem" text={buyerProblem} icon={AlertCircle} testId={`chip-problem-${offer.id}`}
              tint={{ icon: "text-rose-500", iconBg: "bg-rose-500/10 border-rose-500/15", label: "text-rose-500/90" }}
            />
            <InsightChip
              label="Desired Outcome" text={desiredOutcome} icon={Target} testId={`chip-outcome-${offer.id}`}
              tint={{ icon: "text-orange-500", iconBg: "bg-orange-500/10 border-orange-500/15", label: "text-orange-500/90" }}
            />
            <InsightChip
              label="Value Prop" text={valueProp} icon={ShieldCheck} testId={`chip-value-${offer.id}`}
              tint={{ icon: "text-emerald-500", iconBg: "bg-emerald-500/10 border-emerald-500/15", label: "text-emerald-500/90" }}
            />
          </div>

          {/* ── Quote + primary objection strip ──────────────────────── */}
          {/* Matches the persona card: oversized quote mark + italic serif
              quote on the left, a faint hand-drawn line-art sketch in the
              middle, and the PRIMARY OBJECTION block + chevron on the right.
              Collapsed, both text blocks reserve exactly three lines so the
              strip is the same height on every card; the sketch slot only
              renders when the illustration exists (the text blocks flex wider
              in its absence — no broken image, no empty gap). Expanding
              reveals the objection's response and only grows this card. */}
          <div className="mx-4 mt-3 rounded-xl bg-amber-500/[0.05] border border-amber-500/10 px-4 py-3.5 flex items-center gap-3">
            <div className="flex-[1.2] min-w-0 flex gap-2 self-center">
              <span className="text-[38px] leading-[30px] text-emerald-800/75 select-none flex-shrink-0" style={SERIF}>&ldquo;</span>
              <p
                className={`italic text-[13.5px] leading-[18px] self-center ${objectionOpen ? "min-h-[54px]" : "line-clamp-3 max-h-[54px]"} ${quote ? "text-foreground/85" : "text-muted-foreground/45"}`}
                style={SERIF}
                data-testid={`text-quote-${offer.id}`}
              >
                {quote || "No offering hook captured yet"}
              </p>
            </div>
            {sketch && (
              /* The slot stretches to the strip's text-driven height and the
                 (larger) artwork is absolutely centered inside it, so the
                 sketch never changes the strip height — cards with and
                 without a sketch stay identical. Full-strength multiply (no
                 partial opacity) makes the cream PNG background vanish into
                 the tan strip, and a radial mask fades the artwork's edges so
                 nothing terminates in a hard rectangle (same in dark mode,
                 where invert+screen does the blending). */
              <div className="relative hidden sm:block w-[120px] flex-shrink-0 self-stretch" data-testid={`img-sketch-${offer.id}`}>
                <img
                  src={optimizedImg(sketch, 480)}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-[92px] w-full object-contain mix-blend-multiply dark:invert dark:mix-blend-screen dark:opacity-70 pointer-events-none select-none"
                  style={{
                    WebkitMaskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                    maskImage: "radial-gradient(ellipse farthest-side at center, black 55%, transparent 100%)",
                  }}
                />
              </div>
            )}
            <div className="flex-1 min-w-0 flex items-start gap-2 border-l border-amber-500/10 pl-3">
              <div className="flex-1 min-w-0">
                <p className="text-[9.5px] leading-[13px] font-bold uppercase tracking-[0.15em] text-amber-600/90 mb-1">Primary Objection</p>
                <p
                  className={`text-[11px] leading-[16px] ${objectionOpen ? "min-h-[48px]" : "line-clamp-3 h-[48px]"} ${objection ? "text-muted-foreground" : "text-muted-foreground/45 italic"}`}
                  data-testid={`text-objection-${offer.id}`}
                >
                  {objection || "No objection captured yet"}
                </p>
                {objectionOpen && objectionResponse && (
                  <p className="text-[11px] leading-[16px] text-muted-foreground mt-2" data-testid={`text-objection-response-${offer.id}`}>
                    <span className="font-semibold text-emerald-600/90">Response: </span>
                    {objectionResponse}
                  </p>
                )}
              </div>
              {objection && (
                <button
                  className="w-7 h-7 rounded-full border border-border/60 bg-card flex items-center justify-center flex-shrink-0 hover:bg-muted/50 transition-colors self-center"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setObjectionOpen(o => !o); }}
                  data-testid={`button-expand-objection-${offer.id}`}
                  aria-label={objectionOpen ? "Collapse" : "Expand"}
                >
                  <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${objectionOpen ? "rotate-180" : ""}`} />
                </button>
              )}
            </div>
          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-3 px-4 py-3.5">
            <div className="flex items-center gap-2.5 min-w-0">
              <StrengthGauge pct={pct} />
              <div className="min-w-0">
                <p className="text-[12px] font-semibold leading-tight whitespace-nowrap">Offering Strength</p>
                <p className="text-[10.5px] text-muted-foreground leading-tight mt-0.5 truncate">
                  {ready ? "Strong positioning. Ready to activate." : "Add more detail to strengthen."}
                </p>
                {/* Slot is always reserved so the footer is the same height
                    whether or not the offering is complete. */}
                <div className="h-5 mt-1">
                  {ready && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/15 px-2 py-0.5 text-[9px] font-semibold text-emerald-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Offer Ready
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <Button
                className="h-9 rounded-lg px-3.5 gap-1.5 text-[13px] bg-emerald-700 hover:bg-emerald-600 text-white shadow-sm"
                data-testid={`button-open-offer-${offer.id}`}
              >
                <SquareArrowOutUpRight className="h-3.5 w-3.5" />Open
              </Button>
              <div className="flex items-center gap-1.5" onClick={e => e.preventDefault()}>
                {/* Sharing has no offering backend yet — the control renders
                    to match the mockup but stays disabled until it does. */}
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full w-8 h-8"
                  disabled
                  title="Sharing isn't available for offerings yet"
                  data-testid={`button-share-${offer.id}`}
                  aria-label="Share offering"
                >
                  <Share2 className="h-4 w-4 text-muted-foreground" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full w-8 h-8"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setBookmarked(b => !b); }}
                  data-testid={`button-bookmark-${offer.id}`}
                  aria-label="Bookmark offering"
                >
                  <Bookmark className={`h-4 w-4 ${bookmarked ? "fill-current text-emerald-500" : "text-muted-foreground"}`} />
                </Button>
              </div>
              <Badge
                className={`text-[9px] font-semibold uppercase tracking-widest rounded-full px-3 h-6 border inline-flex items-center gap-1.5 ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                    : "bg-amber-500/10 text-amber-500 border-amber-500/20"
                }`}
                data-testid={`badge-status-${offer.id}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-400" : "bg-amber-500"}`} />
                {offer.status || "draft"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function OffersPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [search, setSearch] = useState("");

  // The grid is the ONE consumer that opts into Build-with-AI drafts
  // (?includeDrafts=1) so it can render animated build cards; every other
  // offers consumer gets only complete rows from the default endpoint. The
  // queryKey keeps the plain endpoint string as its first segment so the
  // app-wide invalidations of [`/api/brands/:id/offers`] still match by
  // prefix, while the extra segment keeps this cache entry separate from the
  // drafts-free one other pages share.
  const { data: offers, isLoading } = useQuery<Offer[]>({
    queryKey: [`/api/brands/${brandId}/offers`, { includeDrafts: true }],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/brands/${brandId}/offers?includeDrafts=1`);
      return res.json();
    },
    enabled: !!brandId,
    // Poll fast while any Build-with-AI generation is running (persona-grid
    // style, so the build card reveals the finished offering without a manual
    // refresh). buildStatus now stays 'building' through the AI hero phase,
    // so this fast poll also spans hero generation and the reveal fires with
    // the photo already in place. Otherwise: sketches and AI hero images land
    // in the background (claimed generations within seconds, never-attempted
    // ones when the backfill sweeps pick them up), so keep a gentle poll
    // going until every offering's sketch AND hero are ready or terminally
    // failed — then stop entirely. Build drafts are excluded from the imagery
    // checks: the hero runs INSIDE the build and the sketch pipeline only
    // starts once the build completes.
    refetchInterval: (query) => {
      const list = query.state.data;
      if (!list) return false;
      if (list.some(o => o.buildStatus === "building")) return 2500;
      const settled = list.filter(o => !o.buildStatus);
      const generating = (url: unknown, status: unknown) => !url && status === "generating";
      const pending = (url: unknown, status: unknown) => !url && status !== "failed";
      if (settled.some(o =>
        generating(o.sketchImageUrl, o.sketchImageStatus) ||
        generating(o.heroImageUrl, o.heroImageStatus),
      )) return 4000;
      if (settled.some(o =>
        pending(o.sketchImageUrl, o.sketchImageStatus) ||
        pending(o.heroImageUrl, o.heroImageStatus),
      )) return 15000;
      return false;
    },
  });

  // Products back the hero-photo fallback chain (offering reference images →
  // first included product's photo → photo-less dark band).
  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  const productPhotoById = useMemo(() => {
    const map: Record<string, string> = {};
    products?.forEach(p => {
      const media = (p.mediaJson as unknown[] | null) || [];
      const refs = (p.adgenImageRefs as unknown[] | null) || [];
      const photo = [...media, ...refs].find((u): u is string => typeof u === "string" && !!u.trim());
      if (photo) map[p.id] = photo;
    });
    return map;
  }, [products]);

  const heroPhotoFor = (offer: Offer): string | null => {
    const refs = (offer.adgenImageRefs as unknown[] | null) || [];
    const ownRef = refs.find((u): u is string => typeof u === "string" && !!u.trim());
    if (ownRef) return ownRef;
    const included = Array.isArray(offer.includedProducts) ? (offer.includedProducts as unknown[]) : [];
    for (const pid of included) {
      if (typeof pid === "string" && productPhotoById[pid]) return productPhotoById[pid];
    }
    return null;
  };

  const filteredOffers = useMemo(() => {
    let result = offers || [];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(o =>
        o.name.toLowerCase().includes(q) ||
        (o.shortDescription || "").toLowerCase().includes(q) ||
        (o.summary || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [offers, search]);

  if (isLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-32 rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map(i => <Skeleton key={i} className="h-[480px] rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/10 px-8 py-8 fade-in-up">
        <div className="absolute top-0 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-1/3 w-48 h-48 bg-violet-500/5 rounded-full blur-3xl translate-y-1/2" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center border border-primary/20">
                <Tag className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-offers-title">Offerings</h1>
                <p className="text-sm text-muted-foreground">Services, packages, subscriptions, and other non-product offerings</p>
              </div>
            </div>
          </div>
          <Link href={`/brands/${brandId}/offers/new`}>
            <Button className="rounded-full gap-2 px-5 shadow-lg shadow-primary/20" data-testid="button-create-offer">
              <Plus className="h-4 w-4" />New Offering
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 fade-in-up-delay-1">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search offerings by name or description..."
            className="pl-9 h-10 bg-card/50"
            data-testid="input-search-offers"
          />
        </div>
      </div>

      {filteredOffers.length === 0 ? (
        <div className="text-center py-20 fade-in-up-delay-2">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
            <Tag className="h-8 w-8 text-primary/50" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1.5">
            {offers?.length === 0 ? "No offerings yet" : "No matching offerings"}
          </h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
            {offers?.length === 0 ? "Create your first offering to power targeted campaigns" : "Try adjusting your search terms"}
          </p>
          {offers?.length === 0 && (
            <Link href={`/brands/${brandId}/offers/new`}>
              <Button className="rounded-full px-6" data-testid="button-create-first-offer">
                <Plus className="h-4 w-4 mr-1.5" />Create Offering
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 fade-in-up-delay-2">
          {filteredOffers.map((offer, i) => (
            <OfferingCard
              key={offer.id}
              offer={offer}
              brandId={brandId!}
              heroPhoto={heroPhotoFor(offer)}
              index={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}
